import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/state/AuthContext'
import { apiFailure, type ApiFailure } from '@/lib/apiFailure'
import { watchApi } from './api'
import { retryDelayMs } from './reconnect'

export interface WatchConnection {
  token: string
  serverUrl: string
  identity: string
}

/**
 * Why there is no new watch token: `busy` when the API throttled the fetch (429; the token shares the old viewer
 * token's budget) and `connect_failed` for any other failure, both retrying on their own; `unavailable` when the event
 * is gone (the API's 404 `not_found`, or a 410), which asking again won't change, so it waits for `refresh()` or a new
 * session.
 */
export type WatchTokenError = 'busy' | 'connect_failed' | 'unavailable'

/**
 * A refusal asking again won't change: the event is gone. Any other 4xx (an edge or firewall's 403, a 404 without the
 * API's body, a 400 from an older or newer API) may pass on a later try, so it retries as a failure to connect.
 */
const refusedForGood = ({ status, type }: ApiFailure) => status === 410 || (status === 404 && type === 'not_found')

/**
 * A watch token for this tab: `u_<user>.<tab>` when signed in, `anon_…` otherwise. Fetched again when
 * the session changes (sign-in, sign-out) and on `refresh()`. Only the newest fetch lands, and the
 * current connection stays until its replacement arrives, so the room never drops while refetching.
 *
 * A failed fetch sets `error` and retries after 2 s, 5 s, 15 s, then every 30 s, plus 0–2 s of jitter, until one
 * succeeds: after a removal the room is already gone and nothing else brings a new token. A 429 sets `busy` instead
 * and waits its Retry-After (at most 60 s) plus the jitter. An event that is gone (404 `not_found`, 410) sets
 * `unavailable` and stops. A new fetch (event, session or `refresh()`) clears the error and starts the backoff again.
 */
export function useWatchToken(eventId: string | null) {
  const { token: session } = useAuth()
  const [fetched, setFetched] = useState<{ eventId: string; connection: WatchConnection } | null>(null)
  const [failed, setFailed] = useState<{ eventId: string; code: WatchTokenError } | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    let failures = 0
    let retry: ReturnType<typeof setTimeout> | null = null
    const load = () => {
      retry = null
      watchApi
        .watchToken(eventId)
        .then((res) => {
          if (cancelled) return
          setFetched({ eventId, connection: { token: res.data.token, serverUrl: res.data.livekit_url, identity: res.data.identity } })
          setFailed(null)
        })
        .catch((error: unknown) => {
          if (cancelled) return
          const failure = apiFailure(error)
          if (refusedForGood(failure)) {
            setFailed({ eventId, code: 'unavailable' })
            return
          }
          const busy = failure.status === 429
          failures += 1
          setFailed({ eventId, code: busy ? 'busy' : 'connect_failed' })
          retry = setTimeout(load, retryDelayMs(failures, { retryAfterMs: busy ? failure.retryAfterMs : null }))
        })
    }
    setFailed(null)
    load()
    return () => {
      cancelled = true
      if (retry !== null) clearTimeout(retry)
    }
  }, [eventId, session, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])
  // Another event's token or failure never belongs to this one.
  const connection = fetched && fetched.eventId === eventId ? fetched.connection : null
  const error = failed && failed.eventId === eventId ? failed.code : null
  return { connection, error, refresh }
}
